import { CommunityAccessType, CommunityMembershipRole, CommunityMembershipStatus } from '../enums';

export const COMMUNITY_OWNERSHIP_TRANSFER_CONFIRM_NOTE_MAX = 500;

export type CommunityOwnershipTransferCreator = {
  id: string;
  userId: string;
  disabled?: boolean;
  setupStatus?: string;
  stripeConnectAccountId?: string | null;
  connectChargesEnabled?: boolean;
};

export type CommunityOwnershipTransferMembership = {
  userId: string;
  role: string;
  status: string;
};

export type CommunityOwnershipTransferCommunity = {
  id: string;
  title: string;
  accessType: string;
  activePaidMembershipCount: number;
  hasPaidTiers: boolean;
};

export function normalizeCommunityOwnershipTransferConfirmNote(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, COMMUNITY_OWNERSHIP_TRANSFER_CONFIRM_NOTE_MAX);
}

export function isCommunityConnectReadyForPaidOwnership(
  creator: Pick<
    CommunityOwnershipTransferCreator,
    'stripeConnectAccountId' | 'connectChargesEnabled'
  >,
): boolean {
  return Boolean(creator.stripeConnectAccountId?.trim() && creator.connectChargesEnabled);
}

export function communityRequiresConnectForOwnershipTransfer(community: {
  accessType: string;
  hasPaidTiers: boolean;
}): boolean {
  return community.accessType === CommunityAccessType.PAID || community.hasPaidTiers;
}

export function validateCommunityOwnershipTransferInitiation(params: {
  actorUserId: string;
  actorCreatorId: string;
  targetUserId: string;
  targetCreator: CommunityOwnershipTransferCreator | null;
  targetMembership: CommunityOwnershipTransferMembership | null;
  community: CommunityOwnershipTransferCommunity;
}):
  | { ok: true }
  | { ok: false; message: string } {
  if (params.targetUserId === params.actorUserId) {
    return { ok: false, message: 'You cannot transfer ownership to yourself' };
  }

  if (
    !params.targetMembership ||
    params.targetMembership.status !== CommunityMembershipStatus.ACTIVE
  ) {
    return { ok: false, message: 'Target user must be an active Circle member' };
  }

  if (
    !params.targetCreator ||
    params.targetCreator.disabled ||
    params.targetCreator.setupStatus !== 'active'
  ) {
    return { ok: false, message: 'Target user must have an active creator account' };
  }

  if (params.targetCreator.id === params.actorCreatorId) {
    return { ok: false, message: 'Target user already owns this Circle through their creator account' };
  }

  if (params.community.activePaidMembershipCount > 0) {
    return {
      ok: false,
      message:
        'Resolve active paid memberships before transferring ownership. Pause or cancel subscriptions first.',
    };
  }

  if (communityRequiresConnectForOwnershipTransfer(params.community)) {
    if (!isCommunityConnectReadyForPaidOwnership(params.targetCreator)) {
      return {
        ok: false,
        message:
          'Incoming owner must finish Stripe Connect setup with charges enabled before accepting a paid Circle',
      };
    }
  }

  return { ok: true };
}

export function validateCommunityOwnershipTransferAcceptance(params: {
  actorUserId: string;
  transfer: {
    status: string;
    toCreator: CommunityOwnershipTransferCreator;
  };
  community: CommunityOwnershipTransferCommunity;
}):
  | { ok: true }
  | { ok: false; message: string } {
  if (params.transfer.status !== 'pending') {
    return { ok: false, message: 'This ownership transfer is no longer pending' };
  }

  if (params.transfer.toCreator.userId !== params.actorUserId) {
    return { ok: false, message: 'Only the incoming owner can accept this transfer' };
  }

  if (
    params.transfer.toCreator.disabled ||
    params.transfer.toCreator.setupStatus !== 'active'
  ) {
    return { ok: false, message: 'Active creator account required to accept ownership' };
  }

  if (communityRequiresConnectForOwnershipTransfer(params.community)) {
    if (!isCommunityConnectReadyForPaidOwnership(params.transfer.toCreator)) {
      return {
        ok: false,
        message: 'Finish Stripe Connect setup with charges enabled before accepting this Circle',
      };
    }
  }

  if (params.community.activePaidMembershipCount > 0) {
    return {
      ok: false,
      message: 'Active paid memberships must be resolved before ownership can transfer',
    };
  }

  return { ok: true };
}

export function mapOutgoingOwnerMembershipRoleAfterTransfer(
  previousRole: string,
): CommunityMembershipRole {
  return previousRole === CommunityMembershipRole.MODERATOR
    ? CommunityMembershipRole.MODERATOR
    : CommunityMembershipRole.MEMBER;
}
